import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { logout } from '../store/authSlice';
import { addBreadcrumb, captureException } from '../utils/errorReporting';
import { getGlobalApiUrl } from '../utils/globalConfig';
import { clearSession, refreshSession } from '../utils/session';

import type {
	BaseQueryFn,
	FetchArgs,
	FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

// Define RootState type locally to avoid circular dependency
interface RootState {
	mfeAuth?: {
		accessToken?: string;
	};
	tenant?: {
		tenant: {
			id: string;
		} | null;
	};
}

const OWN_ERROR_ENDPOINTS = new Set(['addStatementPages', 'rerunStatement']);

const errorMiddleware = (result: any, api: any) => {
	if ('error' in result) {
		const error = result.error as FetchBaseQueryError;
		let errorMessage = 'An error occurred';
		// These callers show their own message for client errors (402 no reports, 413 too large).
		let shouldShowToast = !(
			OWN_ERROR_ENDPOINTS.has(api.endpoint) &&
			typeof error.status === 'number' &&
			error.status < 500
		);
		if ('status' in error) {
			switch (error.status) {
				case 401:
					errorMessage = 'Unauthorized access';
					clearSession();
					api.dispatch(logout());
					window.location.href = '/auth?type=login';
					break;
				case 403:
					errorMessage = 'Access forbidden';
					break;
				case 409:
				case 400:
				case 404:
					shouldShowToast = false;
					break;
				default:
					errorMessage =
						typeof error.data === 'object' && error.data !== null
							? (error.data as { error?: string }).error ||
								'An unexpected error occurred'
							: 'An unexpected error occurred';
			}
		}

		// Record API errors, but don't spam for expected errors like 401, 404.
		if ('status' in error && ![401, 404].includes(error.status as number)) {
			addBreadcrumb(
				`API Error: ${error.status} - ${errorMessage}`,
				'http',
				'error',
			);

			// For 500+ errors, capture as exceptions.
			if (error.status && Number(error.status) >= 500) {
				captureException(new Error(`API Error: ${errorMessage}`), {
					apiError: true,
					status: error.status,
					data: error.data,
					endpoint: 'unknown', // Endpoint information not available in this context
				});
			}
		}

		// Dispatch toast action for error handling
		if (shouldShowToast) {
			api.dispatch({
				type: 'toast/showToast',
				payload: {
					title: 'Error',
					description: errorMessage,
					type: 'error',
					options: { duration: 5000 },
				},
			});
		}
	}
	return result;
};

// Auth calls answer 401 for a wrong password or a dead session; never refresh for them.
const NO_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'];

const needsRefresh = (
	result: { error?: FetchBaseQueryError },
	args: string | FetchArgs,
	usesPlatformToken: boolean,
) => {
	const url = typeof args === 'string' ? args : args.url;
	return (
		result.error?.status === 401 &&
		!usesPlatformToken &&
		!NO_REFRESH_PATHS.some((path) => url.startsWith(path))
	);
};

// Timeout after 20 seconds
const TIMEOUT = 60000;

// Retry configuration
const RETRY_COUNT = 3;
const RETRY_DELAY = 1000; // 1 second

export const paymentStatementBaseQuery: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (
	args,
	api,
	extraOptions: { accessToken?: string; baseUrl?: string } & Record<
		string,
		any
	>,
) => {
	// Use global API URL if available, otherwise use extraOptions.baseUrl, then fallback to environment variable
	const baseUrl =
		getGlobalApiUrl() ||
		extraOptions?.baseUrl ||
		process.env.REACT_APP_STATEMENT_API;

	const state = api.getState() as RootState;

	if (!baseUrl) {
		return {
			error: {
				status: 'CUSTOM_ERROR',
				error: 'Payment statement API base URL not configured',
			} as FetchBaseQueryError,
		};
	}

	const apiUrl = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
	const usesPlatformToken = Boolean(
		extraOptions?.accessToken || state.mfeAuth?.accessToken,
	);
	let retryCount = 0;
	let lastError: FetchBaseQueryError | undefined;

	while (retryCount < RETRY_COUNT) {
		try {
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);

			const runQuery = fetchBaseQuery({
				baseUrl: apiUrl,
				credentials: 'include', // Required for CORS requests with credentials
				prepareHeaders: (headers, { getState }) => {
					const currentState = getState() as RootState;
					// Check for token in multiple sources: extraOptions, Redux state, and localStorage
					const currentAccessToken =
						extraOptions?.accessToken ||
						currentState.mfeAuth?.accessToken ||
						localStorage.getItem('auth_token');
					if (currentAccessToken) {
						headers.set('authorization', `Bearer ${currentAccessToken}`);
					}

					// Get tenant_id from localStorage or Redux state
					const tenantId =
						currentState.tenant?.id || localStorage.getItem('tenant_id');

					// Only set header if tenantId is a valid non-null value
					if (
						tenantId &&
						tenantId !== 'null' &&
						tenantId !== 'none' &&
						tenantId.trim() !== ''
					) {
						headers.set('x-tenant-id', tenantId);
					}

					return headers;
				},
				fetchFn: (input, init) => {
					return fetch(input, {
						...init,
						signal: controller.signal,
					});
				},
			});

			let result = await runQuery(args, api, extraOptions);
			if (needsRefresh(result, args, usesPlatformToken)) {
				const outcome = await refreshSession(apiUrl);
				if (outcome === 'refreshed') {
					result = await runQuery(args, api, extraOptions);
				} else if (outcome === 'unavailable') {
					// Keep the session; the server can still refresh it later.
					result = {
						error: {
							status: 'FETCH_ERROR',
							error: 'Could not refresh the session',
						},
					};
				}
			}

			clearTimeout(timeoutId);
			return errorMiddleware(result, api);
		} catch (error) {
			lastError = error as FetchBaseQueryError;
			retryCount++;

			// Add breadcrumb for retry attempts
			addBreadcrumb(
				`API request retry ${retryCount}/${RETRY_COUNT}`,
				'http',
				'warning',
			);

			if (retryCount < RETRY_COUNT) {
				await new Promise((resolve) =>
					setTimeout(resolve, RETRY_DELAY * retryCount),
				);
				continue;
			}
		}
	}

	// Capture retry failures as exceptions.
	captureException(new Error('API request failed after multiple retries'), {
		retryFailure: true,
		retryCount: RETRY_COUNT,
		lastError: lastError,
		baseUrl: getGlobalApiUrl(),
	});

	// Show error toast for retry failures
	api.dispatch({
		type: 'toast/showToast',
		payload: {
			title: 'Error',
			description: 'Request failed after multiple retries',
			type: 'error',
			options: { duration: 5000 },
		},
	});

	return {
		error:
			lastError ||
			({
				status: 'CUSTOM_ERROR',
				error: 'Request failed after retries',
			} as FetchBaseQueryError),
	};
};

// Create the main API instance for payment statement
export const paymentStatementApi = createApi({
	reducerPath: 'paymentStatementApi',
	baseQuery: paymentStatementBaseQuery,
	endpoints: () => ({}),
	tagTypes: [
		'StatementList',
		'Statement',
		'ProcessingStatus',
		'CustomRates',
		'TeamMembers',
		'UserInfo',
		'SubscriptionStatus',
		'DefaultRates',
		'OnboardingStatus',
		'EnterpriseUsers',
		'Invoices',
		'Invoice',
		'BillingConfig',
		'PaymentStatus',
		'EnterpriseSettings',
		'RevenueConfigs',
		'Tenants',
		'TenantConfig',
		'Domains',
		'Enterprises',
		'APIKeys',
		// Line Item Tags
		'LineItems',
		'LineItem',
		'LineItemAuditLogs',
		// Dimension Tags
		'FeeTypes',
		'Processors',
		'Networks',
	],
});
